import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { stringToEnum } from 'src/app/shared/utility';
import {
    Models,
    Parameter,
    Scope,
    SkillArchitectureDTO,
} from '../../models/skill-architecture-form';
import { SelectedModelService } from '../../../shared/services/selected-model.service';
import { SkillArchitectureService } from '../../services/skill-architecture.service';
import { DropdownEntry } from '../../../shared/components/dropdown/dropdown.component';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';

@Component({
    selector: 'app-model-configuration',
    templateUrl: './model-configuration.component.html',
    styleUrls: [
        '../../../algorithm/shared/algorithm-parameters.scss', // crossdependency for now as it should look like an algorithm parameter, resolve when moved to new location
    ],
    standalone: false,
})
export class ModelConfigurationComponent implements OnInit, OnChanges {
    @Input() editingIsDisabled: boolean = false;
    @Input() trainingId: string;

    models: DropdownEntry[];
    selectedModel: string;
    tilingActive: boolean;
    useImagesWithoutAnnotations: boolean;

    form: FormGroup;
    initialized: boolean = false;
    @Input() displayTilingOption: boolean;

    constructor(
        private skillArchitectureService: SkillArchitectureService,
        private authenticationService: AuthenticationService,
        private selectedModelService: SelectedModelService
    ) {}

    get hasExperimentalRole(): boolean {
        if (this.authenticationService) {
            return this.authenticationService.isExperimental();
        }
        return false;
    }

    filterAllowedModels(value: [string, Models]) {
        const label: string = value[0];
        const model: Models = value[1];
        if (
            model !== Models.grounding_dino ||
            this.authenticationService.isExperimental()
        ) {
            return [label, model];
        }
    }

    ngOnInit(): void {
        // additional experimental guard for grounding dino in case model selection becomes generally available
        this.models = Object.entries(Models)
            .filter(
                (value) =>
                    value[1] !== Models.grounding_dino ||
                    this.hasExperimentalRole
            )
            .map(([value, label]) => {
                return {
                    value: value,
                    label: label,
                };
            });

        // Query database for data about this training's skill architecture
        this.skillArchitectureService
            .getSkillArchitectureFormData(this.trainingId)
            .subscribe((skillArchitecture: SkillArchitectureDTO) => {
                const parameters: Parameter[] = skillArchitecture.parameter;
                parameters.forEach((parameter) => {
                    switch (parameter.scope) {
                        case Scope.PREPROCESSING:
                            this.processPreprocessingConfiguration(parameter);
                            break;
                        case Scope.MODEL_ARCHITECTURE:
                            this.processModelArchitectureConfiguration(
                                parameter
                            );
                            break;
                    }
                });

                this.createForm();

                this.initialized = true;
            });
    }

    private createForm() {
        this.form = new FormGroup({
            tilingActive: new FormControl<boolean>(this.tilingActive),
            selectedModel: new FormControl<string>(this.selectedModel),
            useImagesWithoutAnnotations: new FormControl<Boolean>(
                !!this.useImagesWithoutAnnotations
            ),
        });

        if (this.editingIsDisabled) {
            this.form.disable();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.editingIsDisabled && this.initialized) {
            if (this.editingIsDisabled) {
                this.form.disable();
            } else {
                this.form.enable();
            }
        }
    }

    private processPreprocessingConfiguration(parameter: Parameter): void {
        if (parameter.name === 'tiling_active') {
            this.tilingActive = parameter.value;
        }
        if (parameter.name === 'use_images_without_annotations') {
            this.useImagesWithoutAnnotations = parameter.value;
        }
    }

    private processModelArchitectureConfiguration(parameter: Parameter): void {
        if (parameter.name === 'name') {
            this.setSelectedModel(parameter.value);
        }
    }

    private setSelectedModel(model: string) {
        if (model in Models) {
            this.selectedModel = model;
        }
    }

    onTilingCheckboxChanged(checked: boolean): void {
        if (!this.initialized) {
            return;
        }
        this.tilingActive = checked;
        this.saveCurrentConfiguration();
    }

    onIgnoreImagesWithoutAnnotationsChanged(checked: boolean): void {
        if (!this.initialized) {
            return;
        }
        this.useImagesWithoutAnnotations = checked;
        this.saveCurrentConfiguration();
    }

    onSelectedModelChanged(newModel: string): void {
        if (!this.initialized || this.form.pristine) {
            return;
        }
        if (newModel in Models) {
            const newModelAsModels: Models | undefined = stringToEnum(
                Models,
                newModel
            );
            if (newModelAsModels === Models.grounding_dino) {
                this.onTilingCheckboxChanged(false);
            }
            this.selectedModel = newModel;
            this.saveCurrentConfiguration();
            this.selectedModelService.sendMessage(newModelAsModels);
        }
    }

    saveCurrentConfiguration(): void {
        if (this.editingIsDisabled) {
            return;
        }

        const configuration: Parameter[] = [
            this.createParameter(
                'name',
                this.selectedModel,
                Scope.MODEL_ARCHITECTURE
            ),
            this.createParameter(
                'tiling_active',
                this.tilingActive,
                Scope.PREPROCESSING
            ),
            this.createParameter(
                'use_images_without_annotations',
                !!this.useImagesWithoutAnnotations,
                Scope.PREPROCESSING
            ),
        ];
        const configurationDTO: SkillArchitectureDTO = {
            id: this.trainingId,
            parameter: configuration,
        };

        this.skillArchitectureService
            .saveSkillArchitectureFormData(this.trainingId, configurationDTO)
            .subscribe();
    }

    createParameter(name: string, value: any, scope: string): Parameter {
        return {
            name: name,
            value: value,
            scope: scope,
        };
    }
}
